import axios, { AxiosInstance } from 'axios';
import { API_CONFIG } from '../configs/api.config';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.API_BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const orgId = localStorage.getItem('currentOrgId');
      if (orgId) {
        config.headers['X-Org-Id'] = orgId;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('authToken')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('currentOrgId');
          window.location.href = '/login';
        }
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred';
        return Promise.reject(new Error(message));
      }
    );
  }

  // ==================== Auth ====================
  async login(email: string, password: string) {
    const response = await this.api.post('/api/auth/login', { email, password });
    return response.data;
  }

  async register(data: { email: string; password: string; firstName: string; lastName: string; invitationToken?: string }) {
    const response = await this.api.post('/api/auth/register', data);
    return response.data;
  }

  async logout() {
    const response = await this.api.post('/api/auth/logout');
    return response.data;
  }

  async respondToChallenge(email: string, newPassword: string, session: string) {
    const response = await this.api.post('/api/auth/challenge', { email, newPassword, session });
    return response.data;
  }

  async requestPasswordReset(email: string) {
    const response = await this.api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(email: string, code: string, newPassword: string) {
    const response = await this.api.post('/api/auth/reset-password', { email, code, newPassword });
    return response.data;
  }

  async verifyEmail(email: string, code: string) {
    const response = await this.api.post('/api/auth/verify-email', { email, code });
    return response.data;
  }

  async acceptInvitation(token: string) {
    const response = await this.api.post('/api/auth/accept-invitation', { token });
    return response.data;
  }

  // OAuth
  async getOAuthUrl(provider: string, redirectUri: string) {
    const response = await this.api.get(`/api/auth/oauth/${provider}/authorize`, { params: { redirect_uri: redirectUri } });
    return response.data;
  }

  async oauthCallback(provider: string, code: string, redirectUri: string) {
    const response = await this.api.post(`/api/auth/oauth/${provider}/callback`, { code, redirect_uri: redirectUri });
    return response.data;
  }

  // ==================== Users ====================
  async getCurrentUser() {
    const response = await this.api.get('/api/users/me');
    return response.data;
  }

  async listUsers(page: number = 1, perPage: number = 20) {
    const response = await this.api.get('/api/users', { params: { page, per_page: perPage } });
    return response.data;
  }

  async updateUserRole(userId: string, role: string) {
    const response = await this.api.put(`/api/users/${userId}/role`, { role });
    return response.data;
  }

  async updateUser(userId: string, data: any) {
    const response = await this.api.put(`/api/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await this.api.delete(`/api/users/${userId}`);
    return response.data;
  }

  async updatePreferences(data: any) {
    const response = await this.api.put('/api/users/me/preferences', data);
    return response.data;
  }

  // ==================== Organizations ====================
  async getCurrentOrg() {
    const response = await this.api.get('/api/organizations/current');
    return response.data;
  }

  async updateOrg(data: any) {
    const response = await this.api.put('/api/organizations/current', data);
    return response.data;
  }

  async createOrg(data: { name: string }) {
    const response = await this.api.post('/api/organizations', data);
    return response.data;
  }

  async listOrgInvitations() {
    const response = await this.api.get('/api/organizations/invitations');
    return response.data;
  }

  async createOrgInvitation(email: string, role: string = 'member') {
    const response = await this.api.post('/api/organizations/invitations', { email, role });
    return response.data;
  }

  async deleteOrgInvitation(invitationId: string) {
    const response = await this.api.delete(`/api/organizations/invitations/${invitationId}`);
    return response.data;
  }

  async listOrgMembers() {
    const response = await this.api.get('/api/organizations/members');
    return response.data;
  }

  async updateMemberRole(memberId: string, role: string) {
    const response = await this.api.put(`/api/organizations/members/${memberId}/role`, { role });
    return response.data;
  }

  async removeMember(memberId: string) {
    const response = await this.api.delete(`/api/organizations/members/${memberId}`);
    return response.data;
  }

  async listMemberRoles(memberId: string) {
    const response = await this.api.get(`/api/organizations/members/${memberId}/roles`);
    return response.data;
  }

  async addMemberRole(memberId: string, role: string) {
    const response = await this.api.post(`/api/organizations/members/${memberId}/roles`, { role });
    return response.data;
  }

  async removeMemberRole(memberId: string, role: string) {
    const response = await this.api.delete(`/api/organizations/members/${memberId}/roles/${role}`);
    return response.data;
  }

  async listMyOrgs() {
    const response = await this.api.get('/api/users/me/orgs');
    return response.data;
  }

  // ==================== Projects ====================
  async listProjects(params?: { status?: string; client_id?: string }) {
    const response = await this.api.get('/api/projects', { params });
    return response.data;
  }

  async createProject(data: any) {
    const response = await this.api.post('/api/projects', data);
    return response.data;
  }

  async getProject(id: string) {
    const response = await this.api.get(`/api/projects/${id}`);
    return response.data;
  }

  async updateProject(id: string, data: any) {
    const response = await this.api.put(`/api/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string) {
    const response = await this.api.delete(`/api/projects/${id}`);
    return response.data;
  }

  async listProjectTasks(projectId: string) {
    const response = await this.api.get(`/api/projects/${projectId}/tasks`);
    return response.data;
  }

  async createProjectTask(projectId: string, data: any) {
    const response = await this.api.post(`/api/projects/${projectId}/tasks`, data);
    return response.data;
  }

  async updateProjectTask(projectId: string, taskId: string, data: any) {
    const response = await this.api.put(`/api/projects/${projectId}/tasks/${taskId}`, data);
    return response.data;
  }

  async deleteProjectTask(projectId: string, taskId: string) {
    const response = await this.api.delete(`/api/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  }

  async getProjectBudget(projectId: string) {
    const response = await this.api.get(`/api/projects/${projectId}/budget`);
    return response.data;
  }

  // ==================== Clients ====================
  async listClients() {
    const response = await this.api.get('/api/clients');
    return response.data;
  }

  async createClient(data: any) {
    const response = await this.api.post('/api/clients', data);
    return response.data;
  }

  async getClient(id: string) {
    const response = await this.api.get(`/api/clients/${id}`);
    return response.data;
  }

  async updateClient(id: string, data: any) {
    const response = await this.api.put(`/api/clients/${id}`, data);
    return response.data;
  }

  async deleteClient(id: string) {
    const response = await this.api.delete(`/api/clients/${id}`);
    return response.data;
  }

  async getClientProjects(clientId: string) {
    const response = await this.api.get(`/api/clients/${clientId}/projects`);
    return response.data;
  }

  // ==================== Time Entries ====================
  async listTimeEntries(params?: { start_date?: string; end_date?: string; project_id?: string; user_id?: string }) {
    const response = await this.api.get('/api/time-entries', { params });
    return response.data;
  }

  async createTimeEntry(data: any) {
    const response = await this.api.post('/api/time-entries', data);
    return response.data;
  }

  async bulkCreateTimeEntries(entries: any[]) {
    const response = await this.api.post('/api/time-entries/bulk', { entries });
    return response.data;
  }

  async getTimeEntry(id: string) {
    const response = await this.api.get(`/api/time-entries/${id}`);
    return response.data;
  }

  async updateTimeEntry(id: string, data: any) {
    const response = await this.api.put(`/api/time-entries/${id}`, data);
    return response.data;
  }

  async deleteTimeEntry(id: string) {
    const response = await this.api.delete(`/api/time-entries/${id}`);
    return response.data;
  }

  // Timer
  async startTimer(data: { projectId?: string; taskId?: string; description?: string; isBillable?: boolean }) {
    const response = await this.api.post('/api/time-entries/timer/start', data);
    return response.data;
  }

  async stopTimer() {
    const response = await this.api.post('/api/time-entries/timer/stop');
    return response.data;
  }

  async getCurrentTimer() {
    const response = await this.api.get('/api/time-entries/timer/current');
    return response.data;
  }

  async discardTimer() {
    const response = await this.api.delete('/api/time-entries/timer/discard');
    return response.data;
  }

  // Calendar views
  async getDayEntries(date: string) {
    const response = await this.api.get(`/api/time-entries/day/${date}`);
    return response.data;
  }

  async getWeekEntries(date: string) {
    const response = await this.api.get(`/api/time-entries/week/${date}`);
    return response.data;
  }

  async getMonthEntries(year: number, month: number) {
    const response = await this.api.get(`/api/time-entries/month/${year}/${month}`);
    return response.data;
  }

  // Narratives
  async listNarratives() {
    const response = await this.api.get('/api/time-entries/narratives');
    return response.data;
  }

  async createNarrative(data: { title: string; text: string; projectId?: string }) {
    const response = await this.api.post('/api/time-entries/narratives', data);
    return response.data;
  }

  async updateNarrative(id: string, data: any) {
    const response = await this.api.put(`/api/time-entries/narratives/${id}`, data);
    return response.data;
  }

  async deleteNarrative(id: string) {
    const response = await this.api.delete(`/api/time-entries/narratives/${id}`);
    return response.data;
  }

  // ==================== Timesheets ====================
  async listTimesheets() {
    const response = await this.api.get('/api/timesheets');
    return response.data;
  }

  async getTimesheet(week: string) {
    const response = await this.api.get(`/api/timesheets/${week}`);
    return response.data;
  }

  async submitTimesheet(week: string) {
    const response = await this.api.post(`/api/timesheets/${week}/submit`);
    return response.data;
  }

  async unsubmitTimesheet(week: string) {
    const response = await this.api.post(`/api/timesheets/${week}/unsubmit`);
    return response.data;
  }

  async getPendingTimesheets() {
    const response = await this.api.get('/api/timesheets/pending');
    return response.data;
  }

  async approveTimesheet(timesheetId: string) {
    const response = await this.api.post('/api/timesheets/approve', { timesheetId });
    return response.data;
  }

  async rejectTimesheet(timesheetId: string, reason: string) {
    const response = await this.api.post('/api/timesheets/reject', { timesheetId, reason });
    return response.data;
  }

  async getTeamTimesheets(week?: string) {
    const response = await this.api.get('/api/timesheets/team', { params: { week } });
    return response.data;
  }

  async getUserTimesheet(userId: string, week: string) {
    const response = await this.api.get(`/api/timesheets/user/${userId}/${week}`);
    return response.data;
  }

  async adjustUserTimesheet(userId: string, entries: any[]) {
    const response = await this.api.post(`/api/timesheets/user/${userId}/adjust`, { entries });
    return response.data;
  }

  async resetMemberTimesheet(userId: string, week: string) {
    const response = await this.api.post(`/api/timesheets/user/${userId}/reset`, { week });
    return response.data;
  }

  // ==================== Reports ====================
  async getReportSummary(params?: any) {
    const response = await this.api.get('/api/reports/summary', { params });
    return response.data;
  }

  async getReportByProject(params?: any) {
    const response = await this.api.get('/api/reports/by-project', { params });
    return response.data;
  }

  async getReportByUser(params?: any) {
    const response = await this.api.get('/api/reports/by-user', { params });
    return response.data;
  }

  async getReportByClient(params?: any) {
    const response = await this.api.get('/api/reports/by-client', { params });
    return response.data;
  }

  async getReportByDate(params?: any) {
    const response = await this.api.get('/api/reports/by-date', { params });
    return response.data;
  }

  async getUtilizationReport(params?: any) {
    const response = await this.api.get('/api/reports/utilization', { params });
    return response.data;
  }

  async getBudgetReport(params?: any) {
    const response = await this.api.get('/api/reports/budget', { params });
    return response.data;
  }

  async exportReport(data: any) {
    const response = await this.api.post('/api/reports/export', data, { responseType: 'blob' });
    return response.data;
  }

  async generateInvoice(data: any) {
    const response = await this.api.post('/api/reports/invoice', data);
    return response.data;
  }

  async generateInvoicePdf(data: any) {
    const response = await this.api.post('/api/reports/invoice', { ...data, format: 'pdf' }, { responseType: 'blob' });
    return response.data;
  }

  // ==================== Dashboard ====================
  async getPersonalDashboard() {
    const response = await this.api.get('/api/dashboard');
    return response.data;
  }

  async getTeamDashboard() {
    const response = await this.api.get('/api/dashboard/team');
    return response.data;
  }

  async getOrgDashboard() {
    const response = await this.api.get('/api/dashboard/org');
    return response.data;
  }

  // ==================== AI Chat ====================
  async listChatSessions() {
    const response = await this.api.get('/api/ai/sessions');
    return response.data;
  }

  async createChatSession(title?: string) {
    const response = await this.api.post('/api/ai/sessions', { title });
    return response.data;
  }

  async getChatSession(sessionId: string) {
    const response = await this.api.get(`/api/ai/sessions/${sessionId}`);
    return response.data;
  }

  async deleteChatSession(sessionId: string) {
    const response = await this.api.delete(`/api/ai/sessions/${sessionId}`);
    return response.data;
  }

  async listChatMessages(sessionId: string) {
    const response = await this.api.get(`/api/ai/sessions/${sessionId}/messages`);
    return response.data;
  }

  async sendChatMessage(sessionId: string, content: string, modelId?: string) {
    const response = await this.api.post(`/api/ai/sessions/${sessionId}/message`, { content, modelId });
    return response.data;
  }

  async suggestTimeEntry(context: any) {
    const response = await this.api.post('/api/ai/suggest-entry', context);
    return response.data;
  }

  async categorizeEntry(description: string, projects: any[]) {
    const response = await this.api.post('/api/ai/categorize', { description, projects });
    return response.data;
  }

  async listAIModels() {
    const response = await this.api.get('/api/ai/models');
    return response.data;
  }

  async updateAIModel(data: any) {
    const response = await this.api.post('/api/ai/models', data);
    return response.data;
  }

  async deleteAIModelConfig(modelId: string) {
    const response = await this.api.delete(`/api/ai/models/${modelId}`);
    return response.data;
  }

  // ==================== Billing ====================
  async getPlans() {
    const response = await this.api.get('/api/billing/plans');
    return response.data;
  }

  async getCurrentSubscription() {
    const response = await this.api.get('/api/billing/current');
    return response.data;
  }

  async createCheckout(priceId: string, successUrl: string, cancelUrl: string) {
    const response = await this.api.post('/api/billing/checkout', { priceId, successUrl, cancelUrl });
    return response.data;
  }

  async createBillingPortal(returnUrl: string) {
    const response = await this.api.post('/api/billing/portal', { returnUrl });
    return response.data;
  }

  // ==================== Notifications ====================
  async listNotifications() {
    const response = await this.api.get('/api/notifications');
    return response.data;
  }

  async markNotificationRead(notificationId: string) {
    const response = await this.api.put(`/api/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsRead() {
    const response = await this.api.post('/api/notifications/read-all');
    return response.data;
  }

  async getUnreadNotificationCount() {
    const response = await this.api.get('/api/notifications/unread-count');
    return response.data;
  }

  // ==================== Super Admin ====================
  async superAdminListOrgs(page?: number, perPage?: number) {
    const response = await this.api.get('/api/super-admin/organizations', { params: { page, per_page: perPage } });
    return response.data;
  }

  async superAdminListUsers(page?: number, perPage?: number) {
    const response = await this.api.get('/api/super-admin/users', { params: { page, per_page: perPage } });
    return response.data;
  }

  async superAdminGetStats() {
    const response = await this.api.get('/api/super-admin/stats');
    return response.data;
  }

  async superAdminUpdateOrg(orgId: string, data: any) {
    const response = await this.api.put(`/api/super-admin/organizations/${orgId}`, data);
    return response.data;
  }

  async superAdminToggleUser(userId: string) {
    const response = await this.api.put(`/api/super-admin/users/${userId}/toggle`);
    return response.data;
  }

  async superAdminResetPassword(userId: string, newPassword: string) {
    const response = await this.api.post(`/api/super-admin/users/${userId}/reset-password`, { newPassword });
    return response.data;
  }

  async superAdminGrantSuperAdmin(userId: string) {
    const response = await this.api.post('/api/super-admin/grant-super-admin', { target_user_id: userId });
    return response.data;
  }

  async superAdminRevokeSuperAdmin(userId: string) {
    const response = await this.api.delete('/api/super-admin/revoke-super-admin', { data: { target_user_id: userId } });
    return response.data;
  }

  // ==================== Config (Legacy) ====================
  async getThemeConfig() {
    const response = await this.api.get('/api/config/theme');
    return response.data;
  }

  async saveThemeConfig(config: any) {
    const response = await this.api.post('/api/config/theme', config);
    return response.data;
  }

  async getSettings() {
    const response = await this.api.get('/api/config/settings');
    return response.data;
  }

  async saveSettings(data: any) {
    const response = await this.api.post('/api/config/settings', data);
    return response.data;
  }

  // ==================== Audit ====================
  async getAuditLogs(params?: { page?: number; per_page?: number; action?: string; user_id?: string }) {
    const response = await this.api.get('/api/audit/logs', { params });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
